import React, { useState, useEffect, useRef } from 'react';
import { AudioLines, Square } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';

const VoiceDictation = ({ onTranscript, placeholder }) => {
  const { currentLanguage, getSpeechLanguageCode, t } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);

  // Get speech recognition language code for current language
  const speechLangCode = getSpeechLanguageCode(currentLanguage);

  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      setError(t('voice.notSupported', 'Voice dictation is not supported in this browser. Please use Chrome, Edge, or Safari.'));
      return;
    }

    // Initialize speech recognition
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    // Use the language code from the translation context
    recognition.lang = speechLangCode;

    recognition.onstart = () => {
      setIsListening(true);
      setError('');
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      switch (event.error) {
        case 'no-speech':
          setError(t('voice.noSpeech', 'No speech detected. Please try again.'));
          break;
        case 'audio-capture':
          setError(t('voice.noMicrophone', 'No microphone found. Please ensure a microphone is connected.'));
          break;
        case 'not-allowed':
          setError(t('voice.microphoneError', 'Microphone access denied. Please allow microphone access in your browser settings.'));
          break;
        case 'language-not-supported':
          setError(`Speech recognition may not fully support ${currentLanguage}. Results may vary.`);
          break;
        default:
          setError(`Error: ${event.error}`);
      }
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let currentInterim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          currentInterim += result[0].transcript;
        }
      }

      if (finalTranscript) {
        setTranscript((prev) => {
          const newTranscript = prev + (prev ? ' ' : '') + finalTranscript;
          if (onTranscript) {
            onTranscript(newTranscript);
          }
          return newTranscript;
        });
      }
      
      setInterimTranscript(currentInterim);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript, speechLangCode, currentLanguage, t]);

  // Update recognition language when it changes
  useEffect(() => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.lang = speechLangCode;
    }
  }, [speechLangCode, isListening]);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        // Update language before starting
        recognitionRef.current.lang = speechLangCode;
        recognitionRef.current.start();
      } catch (err) {
        console.error('Error starting recognition:', err);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  const clearTranscript = () => {
    setTranscript('');
    setInterimTranscript('');
    if (onTranscript) {
      onTranscript('');
    }
  };

  // Get language display name
  const getLanguageName = (code) => {
    const names = {
      en: 'English', es: 'Spanish', fr: 'French', zh: 'Chinese',
      ru: 'Russian', uk: 'Ukrainian', vi: 'Vietnamese', tl: 'Filipino',
      hi: 'Hindi', ur: 'Urdu', ja: 'Japanese', ko: 'Korean', th: 'Thai',
      fa: 'Farsi', ar: 'Arabic', he: 'Hebrew', ga: 'Irish', de: 'German',
      pt: 'Portuguese', it: 'Italian', pl: 'Polish', nl: 'Dutch',
      af: 'Afrikaans', sw: 'Swahili', am: 'Amharic', om: 'Oromo',
      ti: 'Tigrinya', ha: 'Hausa', ig: 'Igbo', yo: 'Yoruba',
      zu: 'Zulu', xh: 'Xhosa', so: 'Somali'
    };
    return names[code] || code;
  };

  if (!isSupported) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  const defaultPlaceholder = t('voice.placeholder', 'Click the microphone to start dictating...');

  return (
    <div className="space-y-3">
      {/* Language Indicator */}
      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
        <span className="font-medium">{t('voice.speechLanguage', 'Speech Language')}:</span>
        <span className="px-2 py-0.5 bg-[#ea580c] text-white rounded text-xs font-semibold">
          {getLanguageName(currentLanguage)}
        </span>
        <span className="text-xs text-gray-400">({speechLangCode})</span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={isListening ? stopListening : startListening}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            isListening
              ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
              : 'bg-[#ea580c] text-white hover:bg-[#dc5612]'
          }`}
          data-testid="voice-dictation-button"
        >
          {isListening ? (
            <>
              <Square className="w-5 h-5" />
              {t('voice.stopRecording', 'Stop Recording')}
            </>
          ) : (
            <>
              <AudioLines className="w-5 h-5" />
              {t('voice.startDictation', 'Start Dictation')}
            </>
          )}
        </button>
        
        {transcript && (
          <button
            type="button"
            onClick={clearTranscript}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
          >
            {t('voice.clear', 'Clear')}
          </button>
        )}
        
        {isListening && (
          <div className="flex items-center gap-2 text-red-500">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            <span className="text-sm font-medium">{t('voice.listening', 'Listening')} ({getLanguageName(currentLanguage)})...</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Transcript display */}
      <div className="relative">
        <div 
          className={`min-h-[120px] p-4 border rounded-lg bg-white ${
            isListening ? 'border-[#ea580c] ring-2 ring-[#ea580c]/20' : 'border-gray-300'
          }`}
        >
          {transcript || interimTranscript ? (
            <p className="text-gray-800 whitespace-pre-wrap">
              {transcript}
              {interimTranscript && (
                <span className="text-gray-400 italic">{interimTranscript}</span>
              )}
            </p>
          ) : (
            <p className="text-gray-400 italic">{placeholder || defaultPlaceholder}</p>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="text-xs text-gray-500">
        <p>
          {t('voice.tips', 'Tips: Speak clearly and at a moderate pace. Say punctuation like "period", "comma", "question mark" for better formatting.')}
        </p>
        {currentLanguage !== 'en' && (
          <p className="mt-1 text-[#ea580c]">
            Dictating in {getLanguageName(currentLanguage)}. Speech will be transcribed in your selected language.
          </p>
        )}
      </div>
    </div>
  );
};

export default VoiceDictation;
